import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Move } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { chatBackgroundStyle } from "@/components/ChatExtras";

const FITS = [["cover", "Fill"], ["contain", "Fit"], ["tile", "Tile"]];
const clamp = (v) => Math.max(0, Math.min(100, v));

export function BackgroundAdjustDialog({ thread, open, onClose }) {
  const ap = thread?.appearance || {};
  const [layout, setLayout] = useState({ pos_x: 50, pos_y: 50, zoom: 1, fit: "cover", dim: 0 });
  const [busy, setBusy] = useState(false);
  const box = useRef(null);
  const drag = useRef(null);
  useEffect(() => { if (open) setLayout({ pos_x: ap.pos_x ?? 50, pos_y: ap.pos_y ?? 50, zoom: ap.zoom ?? 1, fit: ap.fit ?? "cover", dim: ap.dim ?? 0 }); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDown = (e) => { drag.current = { x: e.clientX, y: e.clientY, px: layout.pos_x, py: layout.pos_y }; e.currentTarget.setPointerCapture(e.pointerId); };
  const onMove = (e) => {
    if (!drag.current || !box.current) return;
    const r = box.current.getBoundingClientRect();
    const k = 100 / Math.max(1, layout.zoom - 0.2);
    setLayout((l) => ({ ...l, pos_x: clamp(drag.current.px - ((e.clientX - drag.current.x) / r.width) * k), pos_y: clamp(drag.current.py - ((e.clientY - drag.current.y) / r.height) * k) }));
  };
  const onUp = () => { drag.current = null; };
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/threads/${thread.id}/appearance`, layout); toast.success("Background adjusted"); onClose(true); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const preview = chatBackgroundStyle({ ...thread, appearance: { ...ap, has_image: ap.has_image, preset: ap.preset, ...layout } });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="sm:max-w-lg" data-testid="chat-bg-adjust-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Move className="h-5 w-5" />Adjust background</DialogTitle>
          <DialogDescription>Drag the picture to choose which part shows behind your messages. Zoom in to crop tighter.</DialogDescription>
        </DialogHeader>
        <div ref={box} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
          className="relative h-64 w-full rounded-md border border-slate-300 overflow-hidden cursor-grab active:cursor-grabbing select-none touch-none" style={preview} data-testid="chat-bg-preview">
          <div className="absolute inset-x-3 bottom-3 space-y-1.5 pointer-events-none">
            <div className="max-w-[60%] rounded-2xl bg-white/90 px-3 py-1.5 text-xs text-slate-700 shadow">Sample message from them</div>
            <div className="ml-auto max-w-[60%] rounded-2xl bg-blue-600/90 px-3 py-1.5 text-xs text-white shadow">Your reply</div>
          </div>
        </div>
        <div className="grid gap-3">
          <div className="flex items-center gap-2 text-xs">
            <span className="w-14 text-slate-500">Layout</span>
            <div className="inline-flex rounded-full border border-slate-200 p-0.5">
              {FITS.map(([v, l]) => <button key={v} type="button" onClick={() => setLayout((s) => ({ ...s, fit: v }))} className={`px-3 py-1 rounded-full font-medium ${layout.fit === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`chat-bg-fit-${v}`}>{l}</button>)}
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs"><span className="w-14 text-slate-500">Zoom</span><Slider min={1} max={3} step={0.05} value={[layout.zoom]} onValueChange={([v]) => setLayout((s) => ({ ...s, zoom: v }))} disabled={layout.fit !== "cover"} className="flex-1" data-testid="chat-bg-zoom" /><span className="w-10 text-right font-mono">{layout.zoom.toFixed(1)}×</span></label>
          <label className="flex items-center gap-2 text-xs"><span className="w-14 text-slate-500">Dim</span><Slider min={0} max={0.8} step={0.05} value={[layout.dim]} onValueChange={([v]) => setLayout((s) => ({ ...s, dim: v }))} className="flex-1" data-testid="chat-bg-dim" /><span className="w-10 text-right font-mono">{Math.round(layout.dim * 100)}%</span></label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setLayout({ pos_x: 50, pos_y: 50, zoom: 1, fit: "cover", dim: 0 })} data-testid="chat-bg-adjust-reset">Reset</Button>
          <Button variant="outline" onClick={() => onClose(false)} data-testid="chat-bg-adjust-cancel">Cancel</Button>
          <Button disabled={busy} onClick={save} data-testid="chat-bg-adjust-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
